import { useCallback, useEffect, useRef } from 'react';

/**
 * Cloudflare Turnstile, rendered explicitly into a ref and executed on demand.
 *
 * Tokens expire after 300s and are single use, so a token is only minted when the
 * caller asks for one (execution: 'execute'), never on render: a buyflow can sit
 * on a form far longer than 300s. Every getToken() after the first resets the
 * widget before executing it again, which is also what makes a retry after a
 * rejected submission work -- resending a spent token is refused.
 *
 * The widget is removed on unmount, so remounting the form never orphans or
 * duplicates it.
 */
const ONLOAD_CALLBACK = '__elnkTurnstileOnload';
const SCRIPT_SRC = `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=${ONLOAD_CALLBACK}`;
const SCRIPT_TIMEOUT_MS = 15000;
const RENDER_TIMEOUT_MS = 15000;
// Long enough for a visitor to finish an interactive challenge.
const TOKEN_TIMEOUT_MS = 120000;

let scriptPromise = null;
let turnstileScript = null;
// Settles whichever load is current, so a late onload or onerror reaches it.
let currentLoad = null;

/**
 * Load the Turnstile api once per page.
 *
 * A timed-out tag is left in place and waited for again rather than re-injected:
 * removing an async script does not stop it executing once fetched, so a retry
 * that injected a second copy could run api.js twice. Only a tag that errored is
 * removed and replaced.
 *
 * @returns {Promise<object>} Resolves with window.turnstile.
 */
export function loadTurnstile () {
  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }

  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const deadline = setTimeout(() => settle(new Error('Turnstile did not load')), SCRIPT_TIMEOUT_MS);

      function settle (error) {
        clearTimeout(deadline);
        currentLoad = null;

        if (error) {
          scriptPromise = null;
          reject(error);
        } else {
          resolve(window.turnstile);
        }
      }

      currentLoad = settle;
      // Named once per page; api.js calls it by name whenever it finishes.
      window[ONLOAD_CALLBACK] = () => currentLoad?.();

      if (turnstileScript?.isConnected) {
        return;
      }

      const script = document.createElement('script');

      script.src = SCRIPT_SRC;
      script.async = true;
      script.onerror = () => {
        script.remove();

        // A stale tag's late error must not fail a wait on a newer, healthy one.
        if (turnstileScript === script) {
          turnstileScript = null;
          currentLoad?.(new Error('Turnstile failed to load'));
        }
      };
      turnstileScript = script;
      document.head.appendChild(script);
    });
  }

  return scriptPromise;
}

/**
 * Reset module state. Tests only.
 */
export function resetTurnstileLoader () {
  scriptPromise = null;
  currentLoad = null;
  turnstileScript?.remove();
  turnstileScript = null;
  delete window[ONLOAD_CALLBACK];
}

/**
 * @param {object} options
 *        {string} options.siteKey - Public Turnstile sitekey.
 *        {string} options.action - Must match the action the server expects for this form.
 * @returns {object} { containerRef, getToken }
 */
export default function useTurnstile ({ siteKey, action }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const renderingRef = useRef(null);
  const pendingRef = useRef(null);
  const executedRef = useRef(false);
  // Bumped on every cleanup, so work started for an old mount can tell it is stale.
  const generationRef = useRef(0);
  // Rejected on cleanup, so a getToken() waiting on a render gives up at once.
  const unmountedRef = useRef(null);

  const settle = useCallback((outcome, value) => {
    const pending = pendingRef.current;

    if (!pending) {
      return;
    }

    pendingRef.current = null;
    clearTimeout(pending.timer);

    if (outcome === 'resolve') {
      pending.resolve(value);
    } else {
      pending.reject(value);
    }
  }, []);

  /**
   * Render the widget if it is not rendered yet. Safe to call repeatedly: a failed
   * load or render is not cached, so the next call (the next submit) tries again.
   *
   * @returns {Promise<string>} The widget id.
   */
  const ensureWidget = useCallback(() => {
    if (widgetIdRef.current !== null) {
      return Promise.resolve(widgetIdRef.current);
    }

    if (!siteKey) {
      return Promise.reject(new Error('Turnstile sitekey is not configured'));
    }

    if (renderingRef.current) {
      return renderingRef.current;
    }

    const generation = generationRef.current;
    const rendering = loadTurnstile()
      .then((turnstile) => {
        if (generation !== generationRef.current || !containerRef.current) {
          throw new Error('Turnstile widget unmounted');
        }

        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          execution: 'execute',
          appearance: 'interaction-only',
          retry: 'never',
          // getToken() resets before every execute, so an auto-refresh ~300s after
          // each token would only surface a challenge nobody asked for.
          'refresh-expired': 'never',
          callback: (token) => settle('resolve', token),
          'error-callback': (code) => settle('reject', new Error(`Turnstile error ${code}`)),
          'timeout-callback': () => settle('reject', new Error('Turnstile challenge timed out')),
        });
        executedRef.current = false;

        return widgetIdRef.current;
      })
      .finally(() => {
        if (renderingRef.current === rendering) {
          renderingRef.current = null;
        }
      });

    renderingRef.current = rendering;

    return rendering;
  }, [siteKey, action, settle]);

  useEffect(() => {
    let rejectUnmounted;

    unmountedRef.current = new Promise((resolve, reject) => {
      rejectUnmounted = reject;
    });
    unmountedRef.current.catch(() => {});

    // Preload so the submit rarely waits. A failure here is retried by getToken().
    ensureWidget().catch(() => {});

    return () => {
      generationRef.current += 1;
      renderingRef.current = null;
      rejectUnmounted(new Error('Turnstile widget unmounted'));
      settle('reject', new Error('Turnstile widget unmounted'));

      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }

      widgetIdRef.current = null;
    };
  }, [ensureWidget, settle]);

  /**
   * Mint a fresh token. Call on submit, never earlier.
   *
   * @returns {Promise<string>} The Turnstile token.
   */
  const getToken = useCallback(async () => {
    const generation = generationRef.current;
    let renderTimer;

    const widgetId = await Promise.race([
      ensureWidget(),
      // Only null before the first effect has run, which a submit cannot precede.
      unmountedRef.current || new Promise(() => {}),
      new Promise((resolve, reject) => {
        renderTimer = setTimeout(() => reject(new Error('Turnstile widget did not render')), RENDER_TIMEOUT_MS);
      }),
    ]).finally(() => clearTimeout(renderTimer));

    // The form unmounted, or re-rendered the widget, while we waited.
    if (generation !== generationRef.current || widgetId !== widgetIdRef.current) {
      throw new Error('Turnstile widget changed');
    }

    settle('reject', new Error('Turnstile token request superseded'));

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => settle('reject', new Error('Turnstile token timed out')), TOKEN_TIMEOUT_MS);

      pendingRef.current = { resolve, reject, timer };

      if (executedRef.current) {
        window.turnstile.reset(widgetId);
      }

      executedRef.current = true;
      window.turnstile.execute(containerRef.current);
    });
  }, [ensureWidget, settle]);

  return { containerRef, getToken };
}
